import { useMemo, useState } from "react";
import { Copy, Check, BadgeCheck, ShieldCheck, Clock } from "lucide-react";
import { useIntelCoupons } from "../api/hooks";
import { BrandLogo } from "../components/OfferCard";
import { GridSkeleton, EmptyState, ErrorState } from "../components/States";
import { MaskedLine, Reveal } from "../components/Motion";
import { Seo } from "../components/Seo";
import { toast } from "../components/ui/sonner";

const STATUS_STYLE = {
    WORKING: "bg-leaf text-white",
    CROSS_VERIFIED: "bg-india text-white",
    VERIFIED: "bg-saffron-soft text-saffron",
};

const STATUS_ICON = {
    WORKING: ShieldCheck,
    CROSS_VERIFIED: ShieldCheck,
    VERIFIED: BadgeCheck,
};

const fmtDate = (d) => {
    if (!d) return null;
    try {
        return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return null;
    }
};

const CouponCard = ({ c, delay }) => {
    const [copied, setCopied] = useState(false);
    const Icon = STATUS_ICON[c.status] || BadgeCheck;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(c.couponCode);
            setCopied(true);
            toast.success(`Code ${c.couponCode} copied`);
            setTimeout(() => setCopied(false), 2000);
        } catch {}
    };

    return (
        <Reveal delay={Math.min(delay, 0.3)}>
            <div
                data-testid="verified-coupon-card"
                className="flex h-full flex-col rounded-2xl border border-ink/5 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
                <div className="flex items-center gap-3">
                    <BrandLogo brand={c.brand} />
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium uppercase tracking-wide text-smoke">
                            {c.brand?.name || c.store}
                        </p>
                        <span className={`mt-1 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[c.status] || "bg-ink/5 text-smoke"}`}>
                            <Icon className="h-3 w-3" />
                            {c.statusLabel || c.status}
                        </span>
                    </div>
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold leading-snug tracking-tight text-ink">
                    {c.discount}
                </h3>
                {c.conditions && <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-smoke">{c.conditions}</p>}
                <div className="mt-5 flex items-center justify-between gap-3 rounded-2xl border border-dashed border-saffron/50 bg-saffron-soft/50 px-4 py-3">
                    <span className="font-mono text-lg font-bold tracking-widest text-ink" data-testid="verified-coupon-code">
                        {c.couponCode}
                    </span>
                    <button
                        type="button"
                        onClick={copy}
                        data-testid="verified-coupon-copy"
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-xs font-medium text-cream transition-colors hover:bg-india"
                    >
                        {copied ? <Check className="h-3.5 w-3.5 text-leaf" /> : <Copy className="h-3.5 w-3.5" />}
                        {copied ? "Copied" : "Copy"}
                    </button>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-smoke">
                    {c.expiry && (
                        <span className="inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" /> Valid till {fmtDate(c.expiry)}
                        </span>
                    )}
                    {c.checkedAt && <span>Checked {fmtDate(c.checkedAt)}</span>}
                    {c.sourceCount > 1 && <span>{c.sourceCount} sources</span>}
                </div>
            </div>
        </Reveal>
    );
};

const CouponsPage = () => {
    const { data, isLoading, isError, refetch } = useIntelCoupons();
    const [status, setStatus] = useState("");
    const [store, setStore] = useState("");

    const items = useMemo(() => {
        let list = data?.items || [];
        if (status) list = list.filter((c) => c.status === status);
        if (store) list = list.filter((c) => c.store === store);
        return list;
    }, [data, status, store]);

    const statuses = Object.keys(data?.labels || {});
    const stores = data?.stores || [];

    return (
        <>
            <Seo
                title="Verified Coupons — SahiKhoj"
                description="Coupons on SahiKhoj are re-checked against public listings — verified, cross-verified and checkout-tested codes only."
                path="/coupons"
            />
            <section className="hero-glow bg-ink pb-14 pt-36 text-cream sm:pb-16 lg:pt-44">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.24em] text-mist">
                        <span className="h-px w-10 bg-saffron" />
                        Verified coupons
                    </p>
                    <h1 className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-[1.06] tracking-tighter sm:text-5xl">
                        <MaskedLine delay={0.15}>
                            Coupons, <span className="text-saffron">actually checked.</span>
                        </MaskedLine>
                    </h1>
                    <p className="mt-5 max-w-xl text-base leading-relaxed text-mist">
                        Every code here was recently re-checked by SahiKhoj — no expired
                        listings, no guesses.
                    </p>
                    {data && (
                        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-mist/70" data-testid="coupons-count">
                            {items.length} verified code{items.length === 1 ? "" : "s"}
                        </p>
                    )}
                </div>
            </section>

            <section className="bg-cream py-12 sm:py-16" data-testid="coupons-page">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    <div className="flex flex-wrap items-center gap-3" data-testid="coupons-filters">
                        <button
                            type="button"
                            onClick={() => setStatus("")}
                            aria-pressed={!status}
                            className={`rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${!status ? "border-saffron bg-saffron text-white" : "border-ink/10 bg-white text-ink hover:border-saffron"}`}
                        >
                            All
                        </button>
                        {statuses.map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => setStatus(s === status ? "" : s)}
                                aria-pressed={status === s}
                                data-testid={`filter-status-${s.toLowerCase()}`}
                                className={`rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${status === s ? "border-saffron bg-saffron text-white" : "border-ink/10 bg-white text-ink hover:border-saffron"}`}
                            >
                                {data.labels[s]}
                            </button>
                        ))}
                        <select
                            value={store}
                            onChange={(e) => setStore(e.target.value)}
                            className="rounded-full border border-ink/10 bg-white px-4 py-2.5 text-sm font-medium text-ink transition-colors focus:border-saffron focus:outline-none"
                            data-testid="filter-store"
                            aria-label="Filter by store"
                        >
                            <option value="">All stores</option>
                            {stores.map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>

                    {isLoading && <GridSkeleton count={6} />}
                    {isError && <ErrorState onRetry={refetch} />}
                    {data && items.length === 0 && (
                        <EmptyState title="No verified codes match" message="Try clearing the filters — newly verified coupons are added regularly." />
                    )}
                    <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="coupons-grid">
                        {items.map((c, i) => (
                            <CouponCard key={c.id} c={c} delay={i * 0.04} />
                        ))}
                    </div>
                    {data?.disclaimer && (
                        <p className="mt-12 border-l-2 border-saffron pl-5 text-xs leading-relaxed text-smoke" data-testid="coupons-disclaimer">
                            {data.disclaimer}
                        </p>
                    )}
                </div>
            </section>
        </>
    );
};

export default CouponsPage;
